/**
 * Administration.
 *
 * Everything the client's own administrator needs so they never have to call the
 * vendor to add an agent. Controls the acting administrator cannot use are
 * disabled with the reason shown, not hidden — hiding a control teaches nothing.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSession } from '@/app/session';
import { ApiError, api } from '@/lib/api/client';
import { formatDateTime, titleCase } from '@/lib/format';
import { AdminControlCenter } from './AdminControlCenter';
import { AuditTrail } from './AuditTrail';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
  Panel,
  Row,
  STATE_TONE,
} from '@/ui/primitives';

interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  extension: string | null;
  phone: string | null;
  team_id: string | null;
  is_active: boolean;
  mfa_enabled: boolean;
  mfa_enforced: boolean;
  must_change_password: boolean;
  last_login_at: string | null;
  roles: string[];
  agent_state: string | null;
}

interface GrantableRole {
  slug: string;
  description: string;
  capability_count: number;
  grantable: boolean;
}

interface AdminQueue {
  id: string;
  name: string;
  slug: string;
  strategy: string;
  is_active: boolean;
  member_count: number;
}

interface OrganizationSummary {
  name: string;
  slug: string;
  timezone: string;
  currency: string;
  data_region: string;
  is_demo: boolean;
  active_users: number;
  teams: number;
  queues: number;
}

export function AdminPage() {
  const { can } = useSession();
  const [workspace, setWorkspace] = useState<'control' | 'users' | 'audit'>('control');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<GrantableRole[]>([]);
  const [queues, setQueues] = useState<AdminQueue[]>([]);
  const [summary, setSummary] = useState<OrganizationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ email: string; password: string } | null>(null);

  const [form, setForm] = useState({
    email: '',
    full_name: '',
    role: 'agent',
    extension: '',
    phone: '',
    queue_slugs: [] as string[],
  });

  const refresh = useCallback(async () => {
    try {
      const [userRows, roleRows, queueRows, org] = await Promise.all([
        api.get<AdminUser[]>('/admin/users?include_inactive=true'),
        api.get<GrantableRole[]>('/admin/roles'),
        api.get<AdminQueue[]>('/admin/queues').catch(() => [] as AdminQueue[]),
        api.get<OrganizationSummary>('/admin/organization'),
      ]);
      setUsers(userRows);
      setRoles(roleRows);
      setQueues(queueRows);
      setSummary(org);
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Unable to load administration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createUser = async () => {
    setError(null);
    try {
      const created = await api.post<{
        user: AdminUser;
        generated_password?: string;
      }>('/admin/users', {
        email: form.email,
        full_name: form.full_name,
        role: form.role,
        extension: form.extension || null,
        phone: form.phone || null,
        queue_slugs: form.queue_slugs,
      });
      if (created.generated_password) {
        // Shown once. There is no way to retrieve it again.
        setNotice({ email: created.user.email, password: created.generated_password });
      }
      setForm({ email: '', full_name: '', role: 'agent', extension: '', phone: '', queue_slugs: [] });
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create the user');
    }
  };

  const act = async (action: () => Promise<unknown>, onPassword?: (email: string, pw: string) => void) => {
    setError(null);
    try {
      const result = (await action()) as { generated_password?: string; user_id?: string };
      if (result?.generated_password && onPassword) {
        onPassword('', result.generated_password);
      }
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Action failed');
    }
  };

  if (loading) return <LoadingState label="Loading administration" />;
  if (error && users.length === 0) return <ErrorState message={error} onRetry={() => void refresh()} />;

  const canManage = can('user:manage');

  const workspaceNav = (
    <nav className="flex shrink-0 gap-1 border-b border-line bg-surface px-2 py-1" aria-label="Admin workspace">
      <Button size="sm" variant={workspace === 'control' ? 'solid' : 'ghost'} tone="brand" onClick={() => setWorkspace('control')}>
        Configuration
      </Button>
      <Button size="sm" variant={workspace === 'users' ? 'solid' : 'ghost'} tone="brand" onClick={() => setWorkspace('users')}>
        Users, roles & passwords
      </Button>
      {/* Only offered to a principal who actually holds audit:view. An
          Administrator does; a Manager reaching /admin by other means does
          not, and the server refuses them regardless. */}
      {can('audit:view') ? (
        <Button size="sm" variant={workspace === 'audit' ? 'solid' : 'ghost'} tone="brand" onClick={() => setWorkspace('audit')}>
          Audit trail
        </Button>
      ) : null}
    </nav>
  );

  if (workspace === 'control') {
    return <div className="flex min-h-0 flex-1 flex-col">{workspaceNav}<AdminControlCenter /></div>;
  }

  if (workspace === 'audit') {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-2 p-2">
        {workspaceNav}
        <AuditTrail />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-px bg-line">
      {workspaceNav}
      {error ? (
        <div className="shrink-0 border-b border-danger/40 bg-danger-soft px-3 py-1.5 text-xs text-danger" role="alert">
          {error}
        </div>
      ) : null}

      {notice ? (
        <div className="flex shrink-0 items-center gap-3 border-b border-warn/40 bg-warn-soft px-3 py-2 text-xs text-warn">
          <span>
            Password for <strong>{notice.email}</strong>:
          </span>
          <code className="knx-num rounded-sm border border-warn/40 px-1.5 py-0.5">
            {notice.password}
          </code>
          <span>Give it to them directly — it cannot be shown again.</span>
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setNotice(null)}>
            Dismiss
          </Button>
        </div>
      ) : null}

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_320px] gap-px bg-line">
        <Panel
          title="Users"
          actions={summary ? <span className="text-2xs text-faint">{summary.active_users} active</span> : null}
        >
          {users.length === 0 ? (
            <EmptyState message="No users yet" hint="Create the first agent on the right." />
          ) : (
            <table className="w-full border-collapse text-base">
              <thead className="sticky top-0 bg-surface-2 text-left text-2xs uppercase tracking-wide text-faint">
                <tr>
                  <th className="h-7 px-2 font-medium">Name</th>
                  <th className="h-7 px-2 font-medium">Email</th>
                  <th className="h-7 px-2 font-medium">Ext</th>
                  <th className="h-7 px-2 font-medium">Calling number</th>
                  <th className="h-7 px-2 font-medium">Roles</th>
                  <th className="h-7 px-2 font-medium">State</th>
                  <th className="h-7 px-2 font-medium">Last sign-in</th>
                  <th className="h-7 px-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="h-row border-b border-line/60">
                    <td className="px-2">
                      {user.full_name}
                      {!user.is_active ? <Badge tone="neutral" className="ml-1">inactive</Badge> : null}
                      {user.must_change_password ? (
                        <Badge tone="warn" className="ml-1">must reset</Badge>
                      ) : null}
                    </td>
                    <td className="px-2 text-muted">{user.email}</td>
                    <td className="knx-num px-2">{user.extension ?? '—'}</td>
                    <td className="knx-num px-2">{user.phone ?? 'Not configured'}</td>
                    <td className="px-2 text-muted">{user.roles.map(titleCase).join(', ')}</td>
                    <td className="px-2">
                      {user.agent_state ? (
                        <Badge tone={STATE_TONE[user.agent_state] ?? 'neutral'}>
                          {user.agent_state.replace('_', ' ')}
                        </Badge>
                      ) : (
                        <span className="text-faint">—</span>
                      )}
                    </td>
                    <td className="knx-num px-2 text-muted">{formatDateTime(user.last_login_at)}</td>
                    <td className="px-2">
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!canManage}
                          title="Edit name, internal extension and call-delivery number"
                          onClick={() => {
                            const fullName = window.prompt('Full name', user.full_name);
                            if (!fullName?.trim()) return;
                            const extension = window.prompt('Internal transfer extension (for example 123)', user.extension ?? '');
                            if (extension === null) return;
                            const phone = window.prompt(
                              'Call-delivery phone in international format, for example +919876500001',
                              user.phone ?? '',
                            );
                            if (phone === null) return;
                            void act(() => api.patch(`/admin/users/${user.id}`, {
                              full_name: fullName.trim(),
                              extension: extension.trim() || user.extension,
                              phone: phone.trim() || user.phone,
                            }));
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!can('role:manage')}
                          title="Replace this user's role assignments"
                          onClick={() => {
                            const allowed = roles.filter((role) => role.grantable).map((role) => role.slug);
                            const next = window.prompt(
                              `Roles (comma separated): ${allowed.join(', ')}`,
                              user.roles.join(', '),
                            );
                            if (!next) return;
                            const selected = next.split(',').map((value) => value.trim()).filter(Boolean);
                            void act(() => api.post(`/admin/users/${user.id}/roles`, { roles: selected }));
                          }}
                        >
                          Roles
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!canManage}
                          title={canManage ? 'Reset password' : 'Your role does not include user:manage'}
                          onClick={() =>
                            act(
                              () => api.post(`/admin/users/${user.id}/reset-password`, {}),
                              (_e, password) => setNotice({ email: user.email, password }),
                            )
                          }
                        >
                          Reset
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!can('session:revoke')}
                          title={
                            can('session:revoke')
                              ? 'Sign out on every device'
                              : 'Your role does not include session:revoke'
                          }
                          onClick={() => act(() => api.post(`/admin/users/${user.id}/revoke-sessions`))}
                        >
                          Sign out
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!canManage || !user.is_active}
                          title={
                            !canManage
                              ? 'Your role does not include user:manage'
                              : !user.is_active
                                ? 'Already inactive'
                                : 'Deactivate (reversible)'
                          }
                          onClick={() => act(() => api.del(`/admin/users/${user.id}`))}
                        >
                          Deactivate
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <div className="flex min-h-0 flex-col gap-px bg-line">
          <Panel title="Add a user" className="shrink-0">
            <div className="flex flex-col gap-1.5 p-2">
              <label className="text-xs text-faint" htmlFor="new-name">
                Full name
              </label>
              <input
                id="new-name"
                className="h-8 rounded-md border border-line bg-bg px-2 text-base"
                value={form.full_name}
                onChange={(event) => setForm({ ...form, full_name: event.target.value })}
              />

              <label className="text-xs text-faint" htmlFor="new-email">
                Email
              </label>
              <input
                id="new-email"
                type="email"
                className="h-8 rounded-md border border-line bg-bg px-2 text-base"
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
              />

              <label className="text-xs text-faint" htmlFor="new-ext">
                Extension
              </label>
              <input
                id="new-ext"
                className="knx-num h-8 rounded-md border border-line bg-bg px-2 text-base"
                value={form.extension}
                onChange={(event) => setForm({ ...form, extension: event.target.value })}
              />

              <label className="text-xs text-faint" htmlFor="new-phone">
                Calling number (international format)
              </label>
              <input
                id="new-phone"
                type="tel"
                placeholder="+919876500001"
                className="knx-num h-8 rounded-md border border-line bg-bg px-2 text-base"
                value={form.phone}
                onChange={(event) => setForm({ ...form, phone: event.target.value })}
              />

              <label className="text-xs text-faint" htmlFor="new-role">
                Role
              </label>
              <select
                id="new-role"
                className="h-8 rounded-md border border-line bg-bg px-2 text-base"
                value={form.role}
                onChange={(event) => setForm({ ...form, role: event.target.value })}
              >
                {roles.map((role) => (
                  <option key={role.slug} value={role.slug} disabled={!role.grantable}>
                    {titleCase(role.slug)}
                    {role.grantable ? '' : ' — beyond your own permissions'}
                  </option>
                ))}
              </select>

              {queues.length > 0 ? (
                <>
                  <span className="text-xs text-faint">Queues</span>
                  <div className="flex flex-wrap gap-1">
                    {queues.map((queue) => {
                      const selected = form.queue_slugs.includes(queue.slug);
                      return (
                        <Button
                          key={queue.id}
                          size="sm"
                          tone={selected ? 'brand' : 'neutral'}
                          variant={selected ? 'solid' : 'outline'}
                          onClick={() =>
                            setForm({
                              ...form,
                              queue_slugs: selected
                                ? form.queue_slugs.filter((slug) => slug !== queue.slug)
                                : [...form.queue_slugs, queue.slug],
                            })
                          }
                        >
                          {queue.name}
                        </Button>
                      );
                    })}
                  </div>
                </>
              ) : null}

              <Button
                tone="brand"
                variant="solid"
                className="mt-1"
                disabled={!canManage || !form.email || !form.full_name}
                title={canManage ? 'Create user' : 'Your role does not include user:manage'}
                onClick={createUser}
              >
                Create user
              </Button>
              <p className="text-2xs text-faint">
                A strong password is generated and shown once. The user must change it on first
                sign-in.
              </p>
            </div>
          </Panel>

          <Panel title="Queues" className="shrink-0">
            {queues.length === 0 ? (
              <EmptyState message="No queues" />
            ) : (
              queues.map((queue) => (
                <Row key={queue.id}>
                  <span className="flex-1 truncate">{queue.name}</span>
                  <span className="text-2xs text-faint">{queue.strategy.replace('_', ' ')}</span>
                  <span className="knx-num text-muted">{queue.member_count}</span>
                </Row>
              ))
            )}
          </Panel>

          {summary ? (
            <Panel title="Organization" className="shrink-0">
              <Row>
                <span className="flex-1 text-faint">Name</span>
                <span>{summary.name}</span>
              </Row>
              <Row>
                <span className="flex-1 text-faint">Timezone</span>
                <span className="knx-num">{summary.timezone}</span>
              </Row>
              <Row>
                <span className="flex-1 text-faint">Data region</span>
                <span>{summary.data_region}</span>
              </Row>
              <Row>
                <span className="flex-1 text-faint">Teams / queues</span>
                <span className="knx-num">
                  {summary.teams} / {summary.queues}
                </span>
              </Row>
            </Panel>
          ) : null}
        </div>
      </div>
    </div>
  );
}
